import type { Product, ProductRoles } from 'types/accessManager.type';

// Display names and order of user management by product. Which products and
// roles a caller gets to see always comes from the backend (`manageable`,
// `products`); this only names them.

export const PRODUCT_ORDER: Product[] = ['platform', 'onboarding', 'voice_recorder', 'tts'];

// The `onboarding` product is shown as Agent Builder; its key stays.
export const PRODUCT_LABELS: Record<Product, string> = {
    platform: 'Platform',
    onboarding: 'Agent Builder',
    voice_recorder: 'Voice Recorder',
    tts: 'TTS',
};

const ROLE_LABELS: Record<string, string> = {
    ADMIN: 'Admin',
    MANAGER: 'Manager',
    AGENT: 'Agent',
    TALENT: 'Talent',
};

export const roleLabel = (key: string): string => ROLE_LABELS[key] ?? key;

// The products that hold at least one role, in display order.
export const productsWithRoles = (roles: ProductRoles): Product[] =>
    PRODUCT_ORDER.filter((p) => (roles[p]?.length ?? 0) > 0);

export const hasAnyRole = (roles: ProductRoles): boolean => productsWithRoles(roles).length > 0;

// While the caller manages the Platform alone, the screens do not name it:
// roles are just "roles", and nobody wonders what "Platform" is. Any other
// product is named, even alone — on the Platform's own screen an Onboarding
// "Admin" read without its product looks like a Platform admin.
export const namesProducts = (manageable: ProductRoles): boolean => {
    const products = productsWithRoles(manageable);
    return products.length > 1 || (products.length === 1 && products[0] !== 'platform');
};

const sameKeys = (a: string[] = [], b: string[] = []): boolean =>
    a.length === b.length && a.every((k) => b.includes(k));

// What an edit sends: only the products whose roles changed, each with its
// full new list ([] takes the person out of it). The backend leaves the
// products not listed as they are.
export const changedProducts = (before: ProductRoles, after: ProductRoles): ProductRoles => {
    const out: ProductRoles = {};
    PRODUCT_ORDER.forEach((p) => {
        if (!sameKeys(before[p], after[p])) {
            out[p] = after[p] ?? [];
        }
    });
    return out;
};

// What a create sends: the products with roles, nothing empty.
export const nonEmptyProducts = (roles: ProductRoles): ProductRoles => {
    const out: ProductRoles = {};
    productsWithRoles(roles).forEach((p) => {
        out[p] = roles[p];
    });
    return out;
};
